/**
 * Annual Loss Summary tab (OR-1175) — year-by-year rollup of Loss Runs.
 *
 * 100% backend (jun-16): every value comes from the `/loss-runs/summary`
 * breakdown (one row per report year), surfaced through `useLossRuns`.
 * Read-only — no Add Row, no delete, no pagination, nothing computed
 * FE-side.
 *
 * The footer shows the only column the backend totals (Total Incurred =
 * summary `total_paid_and_reserved`); the per-column annual totals aren't
 * returned, so those footer cells stay blank (flagged to Henry).
 */
import { Table } from "@tricura/atlas-ui";
import { useLossRuns } from "../../../../hooks/use-loss-runs";
import { displayMoney, num } from "../loss-run-calc";
import { displayPercent } from "../loss-summary-calc";

const W = {
    policyYear: 120,
    indemnity: 130,
    expense: 130,
    reservedIndemnity: 140,
    reservedExpenses: 140,
    totalIncurred: 150,
    incurredClaims: 140,
    reportedIncidents: 150,
    priorYear: 170,
    pctYoY: 170,
} as const;

const fixed = (px: number) => ({ width: px, minWidth: px });
const READONLY_CELL = "text-secondary text-sm tabular-nums";

/** Count cell — "—" for null/0, else the number with commas. */
function displayCount(n: number | null): string {
    if (n === null || !Number.isFinite(n) || n === 0) return "—";
    return n.toLocaleString("en-US");
}

/** Money from a Decimal-as-string — "" (backend null) → "—". */
function money(s: string): string {
    return s.trim() === "" ? "—" : displayMoney(num(s));
}

/** Ratio-as-string → whole percent. "" (backend null) → "—". */
function percent(s: string): string {
    return s.trim() === "" ? "—" : displayPercent(num(s));
}

interface AnnualLossSummaryTableProps {
    submissionId: string | undefined;
}

export function AnnualLossSummaryTable({ submissionId }: AnnualLossSummaryTableProps) {
    const { state } = useLossRuns(submissionId);
    const summary = state.status === "ready" ? state.summary : null;
    const rows = summary?.rows ?? [];

    return (
        <div className="relative flex flex-col overflow-hidden lg:min-h-0 lg:flex-1">
            <Table
                aria-label="Annual Loss Summary"
                wrapperClassName="overflow-auto border-b border-secondary lg:min-h-0 lg:flex-1"
                className="[&_tbody_tr:last-child]:border-b-0!"
            >
                <Table.Header className="bg-secondary sticky top-0 z-20">
                    {/* Tooltip icons hidden per Andressa (Loss Runs Figma) —
                      * definitions aren't ready, so the ⓘ glyphs are off for now. */}
                    <Table.Head id="policyYear" label="Policy Year" isRowHeader style={fixed(W.policyYear)} />
                    <Table.Head id="indemnity" label="Indemnity Incurred" style={fixed(W.indemnity)} />
                    <Table.Head id="expense" label="Expense Incurred" style={fixed(W.expense)} />
                    <Table.Head id="reservedIndemnity" label="Reserved Indemnity" style={fixed(W.reservedIndemnity)} />
                    <Table.Head id="reservedExpenses" label="Reserved Expenses" style={fixed(W.reservedExpenses)} />
                    <Table.Head id="totalIncurred" label="Total Incurred" style={fixed(W.totalIncurred)} />
                    <Table.Head id="incurredClaims" label="# of Incurred Claims" style={fixed(W.incurredClaims)} />
                    <Table.Head id="reportedIncidents" label="# Reported Incidents" style={fixed(W.reportedIncidents)} />
                    <Table.Head id="priorYear" label="Total Incurred Prior Year" style={fixed(W.priorYear)} />
                    <Table.Head id="pctYoY" label="% Change YOY Ground up" style={fixed(W.pctYoY)} />
                </Table.Header>
                <Table.Body>
                    {rows.map((r) => (
                        <Table.Row id={String(r.reportYear)} key={r.reportYear}>
                            <Table.Cell style={fixed(W.policyYear)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-anual-loss-summary-tab-policy-year">{r.reportYear}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.indemnity)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-anual-loss-summary-tab-indemnity-incurred">{money(r.indemnityIncurred)}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.expense)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-anual-loss-summary-tab-expense-incurred">{money(r.expenseIncurred)}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.reservedIndemnity)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-anual-loss-summary-tab-reserved-indemnity">{money(r.reservedIndemnity)}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.reservedExpenses)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-anual-loss-summary-tab-reserved-expenses">{money(r.reservedExpenses)}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.totalIncurred)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-anual-loss-summary-tab-total-incurred">{money(r.incurred)}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.incurredClaims)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-anual-loss-summary-tab-incurred-claims">{displayCount(r.incurredClaimsCount)}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.reportedIncidents)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-anual-loss-summary-tab-reported-incidents">{displayCount(r.reportedIncidentsCount)}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.priorYear)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-anual-loss-summary-tab-prior-year">{money(r.incurredPriorYear)}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.pctYoY)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-anual-loss-summary-tab-yoy-ground">{percent(r.incurredPercentChange)}</span>
                            </Table.Cell>
                        </Table.Row>
                    ))}

                    {rows.length > 0 && (
                        <Table.Row id="__totals__" className="bg-secondary font-semibold sticky bottom-0 z-10">
                            <Table.Cell className="text-quaternary text-xs uppercase tracking-wider" style={fixed(W.policyYear)}>
                                {rows.length} years
                            </Table.Cell>
                            <Table.Cell style={fixed(W.indemnity)} />
                            <Table.Cell style={fixed(W.expense)} />
                            <Table.Cell style={fixed(W.reservedIndemnity)} />
                            <Table.Cell style={fixed(W.reservedExpenses)} />
                            {/* Only the backend-totaled column — the per-column annual
                              * totals aren't returned by /summary (flagged to Henry). */}
                            <Table.Cell style={fixed(W.totalIncurred)}>
                                <span className="text-primary text-sm tabular-nums" data-autotest="loss-runs-anual-loss-summary-tab-total-incurred">{money(summary?.totalPaidAndReserved ?? "")}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.incurredClaims)} />
                            <Table.Cell style={fixed(W.reportedIncidents)} />
                            <Table.Cell style={fixed(W.priorYear)} />
                            <Table.Cell style={fixed(W.pctYoY)} />
                        </Table.Row>
                    )}
                </Table.Body>
            </Table>
        </div>
    );
}
